import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defaultKYCDetailFormInitialValues, IKYCDetailsInitialValueProps, SecondaryClub, TIMRCodeDetails } from '.';
import { maxFileSize } from '@config/constant';
import { Dropdown, SubmitButton } from '@components/common';
import { useValidation } from '@hooks/validations';
import profileImage from '@assets/images/profile.png';
import { useKYCDetailsQuery } from '@services/hooks/enduser-details/useKYCDetailsQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIMRCodeListQuery } from '@services/hooks/enduser-details/useIMRCodeListQuery';
import { useEndUserStore } from '@store/enduser';
import { useKYCDetailsMutation } from '@services/hooks/enduser-details/useKYCDetailMutation';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useKYCFileUploadMutation } from '@services/hooks/enduser-details/useKYCFileUploadMutation';
import { extractFileNameFromUrl } from '@helper/utils';
import { ToastType, useTosterStore } from '@store/toster';
import { useGetKycDocumentPresignedUrl } from '@services/hooks/enduser-details/useGetKycDocumentPresignedUrl';
import { useGetKycProfilePresignedUrl } from '@services/hooks/enduser-details/useGetKycProfilePresignedUrl';
import { useDeletePresignedURL } from '@services/hooks/enduser-details/useDeletePresignedURL';
import { useGetRxDb } from '@hooks/getRxdbData';
import { BDEIMRDetail, ClubDetails, IMRCode, KycDetails, PresignedURLDetails } from '@services/hooks/enduser-details';
import { useFormError } from '@hooks/useFormError';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { ProfileData } from '@services/hooks/profile';
import { useGetBdeImrDetails } from '@services/hooks/enduser-details/useGetBdeImrDetails';
import { useLeadUserQuery } from '@services/hooks/endusers/leadUserQuery';
import { useKaeSecondaryClubQuery } from '@services/hooks/enduser-details/useKaeSecondaryClubQuery';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useBasicDetailsQuery } from '@services/hooks/enduser-details/useBasicDeailsQuery';
import {
	formatIdfyErrorMessage,
	getIdfyKycStatus,
	isIdfyDocumentCheckSuccess,
	isIdfyFaceCompareSuccess,
	isIdfyKycStatusPayloadApproved,
	isIdfyKycStatusPayloadRejected,
	isIdfyKycMaxAttemptsReached,
	buildIdfyKycStatusFooterMessage,
	verifyIdfyKycDocument,
	verifyIdfyKycSelfie,
} from '@services/idfyKyc/idfyKycApi';
import { KycApprovedSummaryCard } from './kycApprovedSummaryCard';
import { KycDocumentCameraModal } from './kycDocumentCameraModal';
import { KycDocumentUploadPanel, type KycDocumentVerificationStatus } from './kycDocumentUploadPanel';
import { KycSelfieVerificationPanel, type FaceMatchUiStatus } from './kycSelfieVerificationPanel';
import { useSuccessTosterStore } from '@store/successToster';
import { useIsImpersonationLoginUser } from '../../../../authGuard/hooks/useIsImpersonationLoginUser';

type KycIdfyStep = 'document' | 'selfie';
type IdfyUiPhase = 'wizard' | 'verify_success_flash' | 'approved_card';

const IDFY_VERIFY_SUCCESS_MS = 2200;

export const KYCDetailsForm = (): ReactElement => {
	const nav = useNavigate();
	const { state } = useLocation();
	const { setToaster } = useTosterStore();
	const { setSuccessModalOpen } = useSuccessTosterStore();
	useIMRCodeListQuery();
	const profileImageRef = useRef<HTMLImageElement>(null);
	const { setActiveNavTab, isEditing, setIsEditing, isFormEdit, formId } = useEndUserStore();
	const {
		data: getKYCFormData,
		isFetching: isGetKycFormFetching,
		error: isGetKycFormError,
	} = useKYCDetailsQuery(formId, isFormEdit);
	const { mutate, isPending: isKYCFormPending } = useKYCDetailsMutation(formId, isFormEdit);
	const { result: clubDetails } = useGetRxDb<ClubDetails>('club');
	const { result: IMRCodeList } = useGetRxDb<IMRCode>('imr_code_list');
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const { KYCDetailsValidationSchema } = useValidation();
	const [kycFileData, setKycFileData] = useState<File | null>();
	const [profileImageData, setProfileImageData] = useState<File | null>();
	const [newUploadedKycUrl, setNewUploadedKycUrl] = useState<string | null>(null);
	const [originalKycUrl, setOriginalKycUrl] = useState<string | null>(null);
	const [isImageDeleted, setIsImageDeleted] = useState<boolean>(false);
	const { mutate: deleteImage } = useDeletePresignedURL();
	const {
		mutate: mutateDocumentPresignedURL,
		data: kycDocumentPresignedURL,
		isPending: isKycDocumentUploadPending,
	} = useGetKycDocumentPresignedUrl();
	const {
		mutate: mutateProfilePresignedURL,
		data: kycProfilePresignedURL,
		isPending: isKycProfileUploadPending,
	} = useGetKycProfilePresignedUrl();
	const { mutate: putKycFileUpload, isPending: isKycFileUploadPending } = useKYCFileUploadMutation();
	const { refetch: bdeOfImrDetailFetch } = useGetBdeImrDetails();
	const { refetch: leadUserRefetch } = useLeadUserQuery();
	const { data: basicDetailsData } = useBasicDetailsQuery(formId, isFormEdit);
	const [secondaryClubList, setSecondaryClubList] = useState<DropdownOptions[]>([]);
	const [selectedClubId, setSelectedClubId] = useState<string | null>(null);
	const checkIMRPermissions = useComponentLevelAccessCheck('imr-code-dropdown');
	const checkBDEPermission = useComponentLevelAccessCheck('bde-code-field');
	const checkRBDMPermission = useComponentLevelAccessCheck('rbdm-next-btn');
	const isBDESEUser = useComponentLevelAccessCheck('BDE-SE-USER');
	type FolderType = 'profile' | 'kyc';
	type ToastSetter = (visible: boolean, type: ToastType, message: string) => void;
	const { data: secondaryClubData } = useKaeSecondaryClubQuery(selectedClubId);
	const userRoleType = useUserRoleType();
	const isImpersonationLoginUser = useIsImpersonationLoginUser();
	const membershipNo = useMemo((): string => {
		const fromState = state?.membershipNumber;
		if (fromState != null && String(fromState).trim() !== '') {
			return String(fromState).trim();
		}
		const fromBasic = basicDetailsData?.membershipno;
		if (fromBasic != null && String(fromBasic).trim() !== '') {
			return String(fromBasic).trim();
		}
		return '';
	}, [state?.membershipNumber, basicDetailsData?.membershipno]);

	/** IDfy document → selfie wizard: add-member, or edit-member after tapping Edit (`!isEditing`). */
	const isIdfyEnrollmentFlow = !isFormEdit || !isEditing;
	/** Same IDfy UI (bootstrap + approved card + wizard) for RBDM add flow and edit-member while viewing (read-only). */
	const useIdfyKycFlowLayout = useMemo(
		() => checkRBDMPermission && (isIdfyEnrollmentFlow || (isFormEdit && isEditing)),
		[checkRBDMPermission, isIdfyEnrollmentFlow, isFormEdit, isEditing]
	);
	const [kycIdfyStep, setKycIdfyStep] = useState<KycIdfyStep>('document');
	const [selfieReady, setSelfieReady] = useState(false);
	const [idfySelfiePending, setIdfySelfiePending] = useState(false);
	const [documentVerificationStatus, setDocumentVerificationStatus] = useState<KycDocumentVerificationStatus>('idle');
	const [documentVerificationMessage, setDocumentVerificationMessage] = useState('');
	const [cameraModalOpen, setCameraModalOpen] = useState(false);
	const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
	const [cameraLoading, setCameraLoading] = useState(false);
	const [faceMatchStatus, setFaceMatchStatus] = useState<FaceMatchUiStatus>('idle');
	const [faceMatchMessage, setFaceMatchMessage] = useState('');
	/** Avoid loading `normal_url` in <img> before the PUT to the SAS URL finishes (Azure returns 404 until the blob exists). */
	const [kycDocumentLocalPreviewUrl, setKycDocumentLocalPreviewUrl] = useState<string | null>(null);
	const [selfieVerificationPreviewUrl, setSelfieVerificationPreviewUrl] = useState<string | null>(null);
	const [idfyUiPhase, setIdfyUiPhase] = useState<IdfyUiPhase>('wizard');
	const [idfyStatusBootstrapPending, setIdfyStatusBootstrapPending] = useState(false);
	const [approvedThumbDocUrl, setApprovedThumbDocUrl] = useState('');
	const [approvedThumbFaceUrl, setApprovedThumbFaceUrl] = useState('');
	const [idfyStatusMessage, setIdfyStatusMessage] = useState('');
	const [idfyMaxAttemptsReached, setIdfyMaxAttemptsReached] = useState(false);
	const verifyFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const revokeSelfiePreviewIfBlob = useCallback((u: string | null): void => {
		if (u?.startsWith('blob:')) {
			URL.revokeObjectURL(u);
		}
	}, []);

	const runDocumentVerificationAfterUpload = useCallback(
		async (documentPublicUrl: string): Promise<void> => {
			const url = documentPublicUrl?.trim();
			if (!url) {
				setDocumentVerificationStatus('idle');
				setDocumentVerificationMessage('');
				return;
			}
			setDocumentVerificationStatus('checking');
			setDocumentVerificationMessage('');
			try {
				if (!membershipNo) {
					setDocumentVerificationStatus('valid');
					setDocumentVerificationMessage(
						'Document uploaded. Automated ID verification runs when a membership number is available.'
					);
					if (isIdfyEnrollmentFlow) {
						setKycIdfyStep('selfie');
						setSelfieReady(false);
						setFaceMatchStatus('idle');
						setFaceMatchMessage('');
						setSelfieVerificationPreviewUrl((prev) => {
							revokeSelfiePreviewIfBlob(prev);
							return null;
						});
					}
					return;
				}
				const res = await verifyIdfyKycDocument(url, membershipNo);
				if (isIdfyDocumentCheckSuccess(res)) {
					setDocumentVerificationStatus('valid');
					setDocumentVerificationMessage(
						typeof res?.message === 'string' && res.message.trim() ? res.message : 'Document is valid.'
					);
					if (isIdfyEnrollmentFlow) {
						setKycIdfyStep('selfie');
						setSelfieReady(false);
						setFaceMatchStatus('idle');
						setFaceMatchMessage('');
						setSelfieVerificationPreviewUrl((prev) => {
							revokeSelfiePreviewIfBlob(prev);
							return null;
						});
					}
				} else {
					setDocumentVerificationStatus('invalid');
					const failMsg =
						typeof res?.message === 'string' && res.message.trim() ? res.message : 'Document could not be verified.';
					setDocumentVerificationMessage(formatIdfyErrorMessage(failMsg) || failMsg);
				}
			} catch (error) {
				setDocumentVerificationStatus('invalid');
				const errMsg = error instanceof Error ? error.message : 'Verification failed.';
				setDocumentVerificationMessage(formatIdfyErrorMessage(errMsg) || errMsg);
			}
		},
		[membershipNo, isIdfyEnrollmentFlow, revokeSelfiePreviewIfBlob]
	);

	const formik = useFormik({
		initialValues: defaultKYCDetailFormInitialValues,
		onSubmit: () => {
			handleNext();
		},
		validationSchema: KYCDetailsValidationSchema,
	});
	const getErrorMessage = useFormError<IKYCDetailsInitialValueProps>(formik);

	const IMRCodeListData = useMemo((): TIMRCodeDetails[] => {
		if (IMRCodeList?.length) {
			return IMRCodeList?.map((imrCode) => ({
				id: imrCode.id,
				value: imrCode.imr_code_c,
			}));
		}
		return [];
	}, [IMRCodeList]);

	const kycDocumentPreviewUrl = useMemo((): string => {
		if (kycDocumentLocalPreviewUrl) {
			return kycDocumentLocalPreviewUrl;
		}
		if (
			(documentVerificationStatus === 'rejected' || documentVerificationStatus === 'max_attempts') &&
			approvedThumbDocUrl
		) {
			return approvedThumbDocUrl;
		}
		return (kycDocumentPresignedURL?.[0]?.normal_url ?? formik.values.document_url ?? '').trim();
	}, [
		kycDocumentLocalPreviewUrl,
		documentVerificationStatus,
		approvedThumbDocUrl,
		kycDocumentPresignedURL,
		formik.values.document_url,
	]);

	const kycDocumentPreviewKind = useMemo((): 'image' | 'pdf' => {
		const url =
			(documentVerificationStatus === 'rejected' || documentVerificationStatus === 'max_attempts') &&
			approvedThumbDocUrl
				? approvedThumbDocUrl
				: (kycDocumentPresignedURL?.[0]?.normal_url ?? formik.values.document_url ?? '').trim();
		if (url.toLowerCase().includes('.pdf')) {
			return 'pdf';
		}
		return 'image';
	}, [documentVerificationStatus, approvedThumbDocUrl, kycDocumentPresignedURL, formik.values.document_url]);

	useEffect(() => {
		return (): void => {
			setKycDocumentLocalPreviewUrl((prev) => {
				if (prev) {
					URL.revokeObjectURL(prev);
				}
				return null;
			});
			setSelfieVerificationPreviewUrl((prev) => {
				revokeSelfiePreviewIfBlob(prev);
				return null;
			});
		};
	}, [revokeSelfiePreviewIfBlob]);

	useEffect(() => {
		if (!formId) {
			nav('/add-member-details/basic-details');
		}
	}, [formId, nav, getKYCFormData]);

	useEffect(() => {
		if (userRoleType) {
			formik.setFieldValue('role', userRoleType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userRoleType]);

	const bdeOfImr = useMemo(async (): Promise<BDEIMRDetail | null | undefined> => {
		if (!checkBDEPermission || !bdeOfImrDetailFetch) return null;

		try {
			const response = await bdeOfImrDetailFetch();
			return response?.data?.data?.find((b) => b.mdi_code_c === formik.values.bde_code);
		} catch (error) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-expect-error
			setToaster(true, ToastType.error, error?.message);
		}
	}, [checkBDEPermission, bdeOfImrDetailFetch, setToaster, formik.values.bde_code]);

	const scheduleIdfyApprovedCardTransition = useCallback((docSnap: string, faceSnap: string): void => {
		if (verifyFlashTimeoutRef.current != null) {
			clearTimeout(verifyFlashTimeoutRef.current);
		}
		setIdfyUiPhase('verify_success_flash');
		verifyFlashTimeoutRef.current = setTimeout(() => {
			verifyFlashTimeoutRef.current = null;
			setApprovedThumbDocUrl(docSnap);
			setApprovedThumbFaceUrl(faceSnap);
			setIdfyUiPhase('approved_card');
		}, IDFY_VERIFY_SUCCESS_MS);
	}, []);

	const completeProfileIdfyCheck = useCallback(
		async (publicProfileUrl: string): Promise<void> => {
			if (!isIdfyEnrollmentFlow || kycIdfyStep !== 'selfie' || idfyUiPhase !== 'wizard') {
				return;
			}
			if (!publicProfileUrl?.trim()) {
				return;
			}
			const docSnap = (kycDocumentPresignedURL?.[0]?.normal_url ?? formik.values.document_url ?? '').trim();
			const faceSnap = publicProfileUrl.trim();
			if (!membershipNo) {
				setSelfieReady(true);
				setFaceMatchStatus('approved');
				setFaceMatchMessage(
					'Selfie saved. Automated face comparison with your document runs when a membership number is available.'
				);
				scheduleIdfyApprovedCardTransition(docSnap, faceSnap);
				return;
			}
			setIdfySelfiePending(true);
			setFaceMatchStatus('idle');
			setFaceMatchMessage('');
			try {
				const res = await verifyIdfyKycSelfie(publicProfileUrl, membershipNo);
				if (isIdfyFaceCompareSuccess(res)) {
					setSelfieReady(true);
					const okMsg =
						typeof res?.message === 'string' && res.message.trim()
							? res.message.trim()
							: 'KYC Approved. Your selfie matches your document.';
					setFaceMatchStatus('approved');
					setFaceMatchMessage(okMsg);
					scheduleIdfyApprovedCardTransition(docSnap, faceSnap);
					return;
				}
				setSelfieReady(false);
				const msg =
					typeof res?.message === 'string' && res.message.trim()
						? res.message.trim()
						: 'Face verification did not match your document.';
				setFaceMatchStatus('rejected');
				setFaceMatchMessage(msg);
				setToaster(true, ToastType.error, formatIdfyErrorMessage(msg) || msg);
			} catch (error) {
				setSelfieReady(false);
				const errMessage = error instanceof Error ? error.message : 'Face verification failed.';
				setFaceMatchStatus('rejected');
				setFaceMatchMessage(errMessage);
				setToaster(true, ToastType.error, formatIdfyErrorMessage(errMessage) || errMessage);
			} finally {
				setIdfySelfiePending(false);
			}
		},
		[
			isIdfyEnrollmentFlow,
			kycIdfyStep,
			idfyUiPhase,
			membershipNo,
			setToaster,
			kycDocumentPresignedURL,
			formik.values.document_url,
			scheduleIdfyApprovedCardTransition,
		]
	);

	const handleNext = useCallback(async () => {
		// After save: remove previous blob only if the stored document URL actually changed (not on IDfy "Next").
		const currentKycDocUrl = (kycDocumentPresignedURL?.[0]?.normal_url ?? formik.values.document_url)?.trim();
		const originalTrimmed = originalKycUrl?.trim();
		if (originalTrimmed && currentKycDocUrl && currentKycDocUrl !== originalTrimmed && isFormEdit) {
			try {
				await new Promise<void>((resolve) => {
					deleteImage(
						{ url: originalTrimmed },
						{
							onSuccess: () => {},
							onError: () => {
								resolve();
							},
						}
					);
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error deleting original image:', error);
			}
		}

		const secondaryClubId = secondaryClubList?.find((c) => c.value === formik.values.secondary_club)?.id as string;

		// Note: backend accepts `convert_lead_c` for conversion; include it at final KYC save.
		// Primary club is set from More Details; KYC must still send the key (null) so the API does not KeyError.
		let payload: KycDetails & { convert_lead_c?: string } = {
			documenturl: kycDocumentPresignedURL?.[0]?.normal_url ?? formik.values.document_url,
			club_mapping: null,
			imrcode_c: formik.values.IMR_Code?.length ? formik.values.IMR_Code : null,
			profile_image_url_c: kycProfilePresignedURL?.[0]?.normal_url ?? formik.values.profile_url,
			secondary_club_c: secondaryClubId ?? null,
			// Final step after swap: mark completed / converted after KYC
			status: 'KYC Details',
			convert_lead_c: 'True',
		};
		if (checkBDEPermission) {
			const bdeData = await bdeOfImr;
			payload = {
				...payload,
				imrcode_c: profile[0]?.mdicode_c ?? null,
				mdicode: bdeData?.mdi_code_c ?? null,
				assigned_cmdi: bdeData?.assigned_mdi_c ?? null,
			};
		}
		if (formId) {
			mutate(payload, {
				onSuccess: (data) => {
					if (data.id) {
						setActiveNavTab([0, 1, 2]);
						if (checkBDEPermission) {
							leadUserRefetch?.();
							setToaster(true, ToastType.success, 'Your member details have been saved successfully!');
							nav('/end-users/new-leads', { replace: true });
							return;
						}
						// After swap, KYC is the final step; show success and redirect.
						const redirectPath = isBDESEUser ? '/end-users/all' : '/lms/users';
						setSuccessModalOpen(redirectPath, 'Your member details have been saved successfully!');
					}
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message);
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values,
		kycDocumentPresignedURL,
		kycProfilePresignedURL,
		formId,
		putKycFileUpload,
		kycFileData,
		profileImageData,
		mutate,
		isFormEdit,
		nav,
		setActiveNavTab,
		originalKycUrl,
		deleteImage,
		isIdfyEnrollmentFlow,
		isEditing,
		kycIdfyStep,
		selfieReady,
		membershipNo,
		idfyUiPhase,
		isBDESEUser,
		setSuccessModalOpen,
	]);

	useEffect(() => {
		if (verifyFlashTimeoutRef.current != null) {
			clearTimeout(verifyFlashTimeoutRef.current);
			verifyFlashTimeoutRef.current = null;
		}
		setKycIdfyStep('document');
		setSelfieReady(false);
		setFaceMatchStatus('idle');
		setFaceMatchMessage('');
		setDocumentVerificationStatus('idle');
		setDocumentVerificationMessage('');
		setNewUploadedKycUrl(null);
		setApprovedThumbDocUrl('');
		setApprovedThumbFaceUrl('');
		setIdfyStatusMessage('');
		setIdfyMaxAttemptsReached(false);
		setIdfyUiPhase('wizard');
		setSelfieVerificationPreviewUrl((prev) => {
			revokeSelfiePreviewIfBlob(prev);
			return null;
		});
		setKycDocumentLocalPreviewUrl((prev) => {
			if (prev) {
				URL.revokeObjectURL(prev);
			}
			return null;
		});
	}, [formId, isFormEdit, revokeSelfiePreviewIfBlob]);

	useEffect(() => {
		return (): void => {
			if (verifyFlashTimeoutRef.current != null) {
				clearTimeout(verifyFlashTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const shouldRunIdfyStatus = checkRBDMPermission && (isIdfyEnrollmentFlow || (isFormEdit && isEditing));

		if (!shouldRunIdfyStatus) {
			setIdfyStatusBootstrapPending(false);
			return;
		}
		if (!membershipNo) {
			setIdfyStatusBootstrapPending(false);
			setIdfyUiPhase('wizard');
			return;
		}
		let cancelled = false;
		setIdfyStatusBootstrapPending(true);
		void getIdfyKycStatus(membershipNo)
			.then((payload) => {
				if (cancelled) {
					return;
				}
				if (isIdfyKycStatusPayloadApproved(payload)) {
					const doc = (payload.document_img ?? '').trim();
					const face = (payload.face_img ?? '').trim();
					setIdfyStatusMessage('');
					setIdfyMaxAttemptsReached(false);
					setApprovedThumbDocUrl(doc);
					setApprovedThumbFaceUrl(face);
					setSelfieReady(true);
					setKycIdfyStep('selfie');
					setIdfyUiPhase('approved_card');
				} else if (isIdfyKycMaxAttemptsReached(payload)) {
					const doc = (payload.document_img ?? '').trim();
					const statusMsg = buildIdfyKycStatusFooterMessage(payload);
					setIdfyMaxAttemptsReached(true);
					setIdfyStatusMessage(statusMsg);
					setApprovedThumbDocUrl(doc);
					setApprovedThumbFaceUrl('');
					setSelfieReady(false);
					setKycIdfyStep('document');
					setIdfyUiPhase('wizard');
					setDocumentVerificationStatus('max_attempts');
					setDocumentVerificationMessage(statusMsg);
					if (doc) {
						void formik.setFieldValue('document_url', doc);
						void formik.setFieldValue('kyc_file_name', extractFileNameFromUrl(doc) || 'document');
					}
				} else if (isIdfyKycStatusPayloadRejected(payload)) {
					const doc = (payload.document_img ?? '').trim();
					const statusMsg = buildIdfyKycStatusFooterMessage(payload);
					setIdfyStatusMessage(statusMsg);
					setIdfyMaxAttemptsReached(false);
					setApprovedThumbDocUrl(doc);
					setApprovedThumbFaceUrl('');
					setSelfieReady(false);
					setKycIdfyStep('document');
					setIdfyUiPhase('wizard');
					setDocumentVerificationStatus('rejected');
					setDocumentVerificationMessage(statusMsg);
					if (doc) {
						void formik.setFieldValue('document_url', doc);
						void formik.setFieldValue('kyc_file_name', extractFileNameFromUrl(doc) || 'document');
					}
				} else {
					setIdfyStatusMessage('');
					setIdfyMaxAttemptsReached(false);
					setKycIdfyStep('document');
					setIdfyUiPhase('wizard');
				}
			})
			.catch(() => {
				if (!cancelled) {
					setKycIdfyStep('document');
					setIdfyUiPhase('wizard');
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIdfyStatusBootstrapPending(false);
				}
			});
		return (): void => {
			cancelled = true;
		};
	}, [checkRBDMPermission, isIdfyEnrollmentFlow, isFormEdit, isEditing, membershipNo, formId]);

	const isValidSelfieOrProfileImage = (file: File, maxSize: number | undefined, setToaster: ToastSetter): boolean => {
		if (!file.type.startsWith('image/')) {
			setToaster(true, ToastType.error, 'Please upload an image file.');
			return false;
		}

		if (maxSize && file.size > maxSize) {
			setToaster(true, ToastType.error, 'File size exceeds 5MB limit. Please choose a smaller file.');
			return false;
		}

		return true;
	};

	const isValidKycDocumentFile = (file: File, maxSize: number | undefined, setToaster: ToastSetter): boolean => {
		const isImage = file.type.startsWith('image/');
		const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
		if (!isImage && !isPdf) {
			setToaster(true, ToastType.error, 'Please upload an image or a PDF document.');
			return false;
		}

		if (maxSize && file.size > maxSize) {
			setToaster(true, ToastType.error, 'File size exceeds 5MB limit. Please choose a smaller file.');
			return false;
		}

		return true;
	};

	const getPayload = (folder: FolderType, file: File): { folder: string; filename: string }[] => [
		{ folder, filename: file.name },
	];

	const uploadFile = (
		folder: FolderType,
		file: File,
		url: string,
		putKycFileUpload: ReturnType<typeof useKYCFileUploadMutation>['mutate'],
		setToaster: ToastSetter,
		publicImageUrl?: string,
		skipProfileDocumentImageField?: boolean
	): void => {
		const uploadData =
			folder === 'profile' ? { profileData: { url, profileImageData: file } } : { kycData: { url, kycFileData: file } };

		const onSuccess = (): void => {
			if (folder === 'profile') {
				if (!skipProfileDocumentImageField) {
					formik.setFieldValue('document_image', URL.createObjectURL(file));
				} else if (publicImageUrl) {
					setSelfieVerificationPreviewUrl((prev) => {
						revokeSelfiePreviewIfBlob(prev);
						return publicImageUrl;
					});
				}
				if (publicImageUrl) {
					void completeProfileIdfyCheck(publicImageUrl);
				}
			} else {
				const docFileName = extractFileNameFromUrl(publicImageUrl ?? url) || file.name;
				void formik.setFieldValue('kyc_file_name', docFileName);
				if (publicImageUrl) {
					void formik.setFieldValue('document_url', publicImageUrl);
				}
				// Add / IDfy flow: do not register presigned URL for beforeunload delete — document must stay for selfie & save.
				if (!isIdfyEnrollmentFlow) {
					setNewUploadedKycUrl(url);
					setIsImageDeleted(false);
				} else {
					setNewUploadedKycUrl(null);
				}
				if (isIdfyEnrollmentFlow) {
					setSelfieReady(false);
					setFaceMatchStatus('idle');
					setFaceMatchMessage('');
					setSelfieVerificationPreviewUrl((prev) => {
						revokeSelfiePreviewIfBlob(prev);
						return null;
					});
				}
				setKycDocumentLocalPreviewUrl((prev) => {
					if (prev) {
						URL.revokeObjectURL(prev);
					}
					return null;
				});
				if (publicImageUrl) {
					void runDocumentVerificationAfterUpload(publicImageUrl);
				}
			}
		};

		const onError = (): void => {
			setToaster(true, ToastType.error, `Failed to upload file.`);
		};

		putKycFileUpload(uploadData, { onSuccess, onError });
	};

	const submitKycDocumentFile = useCallback(
		(file: File): void => {
			if (idfyMaxAttemptsReached) {
				return;
			}
			if (!isValidKycDocumentFile(file, maxFileSize, setToaster)) {
				return;
			}
			setKycFileData(file);
			setDocumentVerificationStatus('idle');
			setDocumentVerificationMessage('');
			const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
			setKycDocumentLocalPreviewUrl((prev) => {
				if (prev) {
					URL.revokeObjectURL(prev);
				}
				return isPdf ? '' : URL.createObjectURL(file);
			});
			const payload = getPayload('kyc', file);
			mutateDocumentPresignedURL(payload, {
				onSuccess: (data: PresignedURLDetails[]) => {
					if (data.length && file) {
						uploadFile('kyc', file, data[0].pre_signed_url, putKycFileUpload, setToaster, data[0].normal_url);
					} else {
						setKycDocumentLocalPreviewUrl((prev) => {
							if (prev) {
								URL.revokeObjectURL(prev);
							}
							return null;
						});
						setToaster(true, ToastType.error, 'Could not get upload URL. Please try again.');
					}
				},
				onError: () => {
					setKycDocumentLocalPreviewUrl((prev) => {
						if (prev) {
							URL.revokeObjectURL(prev);
						}
						return null;
					});
					setToaster(true, ToastType.error, 'Could not get upload URL. Please try again.');
				},
			});
		},
		[
			idfyMaxAttemptsReached,
			setToaster,
			maxFileSize,
			mutateDocumentPresignedURL,
			putKycFileUpload,
			getPayload,
			isValidKycDocumentFile,
			uploadFile,
		]
	);

	const openDocumentCamera = useCallback((): void => {
		if (
			isEditing ||
			idfyMaxAttemptsReached ||
			isKycDocumentUploadPending ||
			isKycFileUploadPending ||
			documentVerificationStatus === 'checking'
		) {
			return;
		}
		setCameraStream((prev) => {
			prev?.getTracks().forEach((t) => t.stop());
			return null;
		});
		setCameraModalOpen(true);
	}, [
		isEditing,
		idfyMaxAttemptsReached,
		isKycDocumentUploadPending,
		isKycFileUploadPending,
		documentVerificationStatus,
	]);

	const requestDocumentCameraStream = useCallback(async (): Promise<void> => {
		setCameraStream((prev) => {
			prev?.getTracks().forEach((t) => t.stop());
			return null;
		});
		setCameraLoading(true);
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: 'environment' } },
				audio: false,
			});
			setCameraStream(stream);
		} catch {
			setToaster(
				true,
				ToastType.error,
				'Could not access the camera. Allow permission in your browser settings, then tap Allow camera again.'
			);
		} finally {
			setCameraLoading(false);
		}
	}, [setToaster]);

	const closeDocumentCamera = useCallback((): void => {
		setCameraModalOpen(false);
		setCameraLoading(false);
		setCameraStream((prev) => {
			prev?.getTracks().forEach((t) => t.stop());
			return null;
		});
	}, []);

	const onDocumentCameraCapture = useCallback(
		(file: File): void => {
			closeDocumentCamera();
			submitKycDocumentFile(file);
		},
		[closeDocumentCamera, submitKycDocumentFile]
	);

	const uploadProfileFromFile = useCallback(
		(file: File): void => {
			if (!isValidSelfieOrProfileImage(file, maxFileSize, setToaster)) {
				return;
			}
			setProfileImageData(file);
			const payload = getPayload('profile', file);
			mutateProfilePresignedURL(payload, {
				onSuccess: (data: PresignedURLDetails[]) => {
					if (data.length && file) {
						uploadFile('profile', file, data[0].pre_signed_url, putKycFileUpload, setToaster, data[0].normal_url);
					}
				},
				onError: () => {
					setToaster(true, ToastType.error, 'Could not get upload URL. Please try again.');
				},
			});
		},
		[
			getPayload,
			isValidSelfieOrProfileImage,
			maxFileSize,
			mutateProfilePresignedURL,
			putKycFileUpload,
			setToaster,
			uploadFile,
		]
	);

	const uploadSelfieVerificationFile = useCallback(
		(file: File): void => {
			if (!isValidSelfieOrProfileImage(file, maxFileSize, setToaster)) {
				return;
			}
			setProfileImageData(file);
			setSelfieVerificationPreviewUrl((prev) => {
				revokeSelfiePreviewIfBlob(prev);
				return URL.createObjectURL(file);
			});
			const payload = getPayload('profile', file);
			mutateProfilePresignedURL(payload, {
				onSuccess: (data: PresignedURLDetails[]) => {
					if (data.length && file) {
						uploadFile('profile', file, data[0].pre_signed_url, putKycFileUpload, setToaster, data[0].normal_url, true);
					}
				},
				onError: () => {
					setSelfieVerificationPreviewUrl((prev) => {
						revokeSelfiePreviewIfBlob(prev);
						return null;
					});
					setToaster(true, ToastType.error, 'Could not get upload URL. Please try again.');
				},
			});
		},
		[
			getPayload,
			isValidSelfieOrProfileImage,
			maxFileSize,
			mutateProfilePresignedURL,
			putKycFileUpload,
			revokeSelfiePreviewIfBlob,
			setToaster,
			uploadFile,
		]
	);

	const handleImageChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const file = event.target.files?.[0];
			event.target.value = '';
			if (!file) {
				return;
			}
			uploadProfileFromFile(file);
		},
		[uploadProfileFromFile]
	);

	useEffect(() => {
		if (formId && getKYCFormData && Object.keys(getKYCFormData).length > 0) {
			if (isFormEdit) {
				setNewUploadedKycUrl(null);
				setOriginalKycUrl(getKYCFormData.documenturl);
				setIsImageDeleted(false);
			}
			const secondaryClubObj = secondaryClubList?.find((c) => c.id === getKYCFormData?.secondary_club_c);

			formik.setValues({
				...formik.values,
				IMR_Code: getKYCFormData.imrcode_c,
				document_url: getKYCFormData.documenturl,
				profile_url: getKYCFormData.profile_image_url_c,
				kyc_file_name: extractFileNameFromUrl(getKYCFormData?.documenturl),
				document_image: getKYCFormData.profile_image_url_c,
				bde_code: getKYCFormData.mdicode ? getKYCFormData.mdicode : '',
				secondary_club: secondaryClubObj?.value || '',
			});
			const selectedClub =
				clubDetails?.find((c) => c.name === getKYCFormData?.clubname_c) ??
				clubDetails?.find((c) => c.id === getKYCFormData?.club_mapping) ??
				null;

			setSelectedClubId(selectedClub?.sfid ?? null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getKYCFormData, formId]);

	// Cleanup orphaned KYC blob on tab close/refresh (edit flow only — not during IDfy add enrollment).
	useEffect(() => {
		const handleBeforeUnload = (): void => {
			if (isIdfyEnrollmentFlow) {
				return;
			}
			if (newUploadedKycUrl && !isImageDeleted) {
				deleteImage(
					{ url: newUploadedKycUrl },
					{
						onSuccess: () => {},
						onError: () => {},
					}
				);
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return (): void => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [newUploadedKycUrl, isImageDeleted, deleteImage, isIdfyEnrollmentFlow]);

	const mapSecondaryClubs = (data: SecondaryClub[]): DropdownOptions[] => {
		return data.map((item) => ({
			id: item.sfid,
			value: item.name,
		}));
	};

	useEffect(() => {
		setSecondaryClubList(mapSecondaryClubs(secondaryClubData || []));
	}, [secondaryClubData]);

	useEffect(() => {
		if (getKYCFormData?.secondary_club_c && secondaryClubList.length) {
			const secondaryClubObj = secondaryClubList.find((c) => c.id === getKYCFormData.secondary_club_c);

			formik.setFieldValue('secondary_club', secondaryClubObj?.value || '');
		}
	}, [secondaryClubList, getKYCFormData]);

	const handleSubmitClick = (): void => {
		formik.handleSubmit();
	};

	/** Block clicks/scroll on the rest of the page while KYC uploads or IDfy checks run (camera modal stays usable). */
	const isKycInteractionBlocked = useMemo((): boolean => {
		if (cameraModalOpen) {
			return false;
		}
		return (
			isKycDocumentUploadPending ||
			isKycFileUploadPending ||
			isKycProfileUploadPending ||
			idfySelfiePending ||
			(useIdfyKycFlowLayout && documentVerificationStatus === 'checking') ||
			(useIdfyKycFlowLayout && !!membershipNo && idfyStatusBootstrapPending)
		);
	}, [
		cameraModalOpen,
		isKycDocumentUploadPending,
		isKycFileUploadPending,
		isKycProfileUploadPending,
		idfySelfiePending,
		useIdfyKycFlowLayout,
		documentVerificationStatus,
		membershipNo,
		idfyStatusBootstrapPending,
	]);

	useEffect(() => {
		if (!isKycInteractionBlocked) {
			return;
		}
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return (): void => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isKycInteractionBlocked]);

	const handleTryAgainFromRejected = useCallback((): void => {
		if (verifyFlashTimeoutRef.current != null) {
			clearTimeout(verifyFlashTimeoutRef.current);
			verifyFlashTimeoutRef.current = null;
		}
		setIdfyUiPhase('wizard');
		setKycIdfyStep('document');
		setSelfieReady(false);
		setFaceMatchStatus('idle');
		setFaceMatchMessage('');
		setIdfyStatusMessage('');
		setIdfyMaxAttemptsReached(false);
		setApprovedThumbDocUrl('');
		setApprovedThumbFaceUrl('');
		setDocumentVerificationStatus('idle');
		setDocumentVerificationMessage('');
		setKycDocumentLocalPreviewUrl((prev) => {
			if (prev) {
				URL.revokeObjectURL(prev);
			}
			return null;
		});
		setSelfieVerificationPreviewUrl((prev) => {
			revokeSelfiePreviewIfBlob(prev);
			return null;
		});
	}, [revokeSelfiePreviewIfBlob]);

	const renderKycDocumentUploadPanel = (): ReactElement => (
		<KycDocumentUploadPanel
			disabled={isEditing || idfyMaxAttemptsReached}
			previewUrl={kycDocumentPreviewUrl}
			previewKind={kycDocumentPreviewKind}
			isUploading={isKycDocumentUploadPending || isKycFileUploadPending}
			verificationStatus={documentVerificationStatus}
			verificationMessage={documentVerificationMessage || idfyStatusMessage}
			onSelectFile={submitKycDocumentFile}
			onTakePhotoClick={() => {
				void openDocumentCamera();
			}}
			suppressValidMessage={useIdfyKycFlowLayout}
			onTryAgain={
				documentVerificationStatus === 'rejected' && !idfyMaxAttemptsReached && isIdfyEnrollmentFlow
					? handleTryAgainFromRejected
					: undefined
			}
		/>
	);

	const renderKycProfileBlock = (placement: 'in-card' | 'idfy-above-card'): ReactElement => {
		const profileWrapClass =
			placement === 'idfy-above-card' ? 'kyc-idfy-profile-slot' : 'kyc-doc-main-container__profile';
		return (
			<div className={profileWrapClass}>
				<div className="profile-image">
					<div className="user-image">
						<img
							ref={profileImageRef}
							src={formik.values.document_image ? formik.values.document_image : profileImage}
							alt="profile img"
							title="profile img"
							width="120"
							height="120"
						/>
					</div>

					<div className={`upload-image ${(isEditing || idfyMaxAttemptsReached) && 'disable-btn'}`}>
						<label htmlFor="image-upload">
							<span className="icon-edit-2"></span>
							<span className="hide">image upload</span>
						</label>
						<input
							type="file"
							name="profile-image"
							id="image-upload"
							accept="image/*,application/pdf"
							disabled={isEditing || idfyMaxAttemptsReached || isKycProfileUploadPending || isImpersonationLoginUser}
							onChange={handleImageChange}
						/>
					</div>
				</div>
				{formik.touched.document_image && formik.errors.document_image ? (
					<p className="error-message text-center show">{formik.errors.document_image}</p>
				) : null}
				{placement === 'idfy-above-card' &&
				isIdfyEnrollmentFlow &&
				idfyUiPhase === 'wizard' &&
				kycIdfyStep === 'selfie' &&
				!membershipNo ? (
					<p className="kyc-idfy-hint kyc-idfy-hint--below-profile">
						Take a selfie in the verification step below to continue. Face match with IDfy runs once a membership number
						is available.
					</p>
				) : null}
			</div>
		);
	};

	const renderKycMainCard = (): ReactElement => (
		<div className="kyc-doc-main-container">
			<h2 className="kyc-doc-main-container__kyc-title">Upload Document</h2>
			{renderKycProfileBlock('in-card')}
			<div className="kyc-doc-main-container__upload">{renderKycDocumentUploadPanel()}</div>
		</div>
	);

	const handleRetakeSelfie = useCallback((): void => {
		if (verifyFlashTimeoutRef.current != null) {
			clearTimeout(verifyFlashTimeoutRef.current);
			verifyFlashTimeoutRef.current = null;
		}
		setFaceMatchStatus('idle');
		setFaceMatchMessage('');
		setSelfieReady(false);
		setSelfieVerificationPreviewUrl((prev) => {
			revokeSelfiePreviewIfBlob(prev);
			return null;
		});
	}, [revokeSelfiePreviewIfBlob]);

	const handleChangeDocumentFromSelfie = useCallback((): void => {
		if (verifyFlashTimeoutRef.current != null) {
			clearTimeout(verifyFlashTimeoutRef.current);
			verifyFlashTimeoutRef.current = null;
		}
		setIdfyUiPhase('wizard');
		setKycIdfyStep('document');
		setSelfieReady(false);
		setFaceMatchStatus('idle');
		setFaceMatchMessage('');
		setSelfieVerificationPreviewUrl((prev) => {
			revokeSelfiePreviewIfBlob(prev);
			return null;
		});
	}, [revokeSelfiePreviewIfBlob]);

	const renderIdfyKycColumn = (): ReactElement => {
		const selfiePanelProps = {
			disabled: isEditing || idfyMaxAttemptsReached,
			isProfileUploading: isKycProfileUploadPending || idfySelfiePending,
			isVerifyingFace: idfySelfiePending,
			faceMatchStatus,
			faceMatchMessage,
			selfiePreviewUrl: selfieVerificationPreviewUrl,
			onSelfieFile: uploadSelfieVerificationFile,
			onRetakeSelfie: handleRetakeSelfie,
			onChangeDocument: handleChangeDocumentFromSelfie,
			onCameraError: (): void => {
				setToaster(
					true,
					ToastType.error,
					'Could not access the camera. Allow permission in your browser settings, then try Allow camera again.'
				);
			},
		};

		if (membershipNo && idfyStatusBootstrapPending) {
			return (
				<div className="kyc-idfy-stack">
					{renderKycProfileBlock('idfy-above-card')}
					<p className="kyc-idfy-bootstrap-msg">Checking verification status…</p>
				</div>
			);
		}

		if (idfyUiPhase === 'approved_card') {
			const docFallback = (formik.values.document_url ?? '').trim();
			const faceFallback = (formik.values.profile_url ?? '').trim();
			return (
				<div className="kyc-idfy-stack">
					{renderKycProfileBlock('idfy-above-card')}
					<KycApprovedSummaryCard
						documentImageUrl={approvedThumbDocUrl || docFallback}
						faceImageUrl={approvedThumbFaceUrl || faceFallback}
					/>
				</div>
			);
		}

		if (idfyUiPhase === 'verify_success_flash') {
			return (
				<div className="kyc-idfy-stack">
					{renderKycProfileBlock('idfy-above-card')}
					<div className="kyc-doc-main-container">
						<h2 className="kyc-doc-main-container__kyc-title">Selfie Verification</h2>
						<div className="kyc-doc-main-container__upload">
							<KycSelfieVerificationPanel {...selfiePanelProps} />
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="kyc-idfy-stack">
				{renderKycProfileBlock('idfy-above-card')}
				<div className="kyc-doc-main-container">
					<h2 className="kyc-doc-main-container__kyc-title">
						{kycIdfyStep === 'document' ? 'Upload Document' : 'Selfie Verification'}
					</h2>
					<div className="kyc-doc-main-container__upload">
						{kycIdfyStep === 'document' ? (
							renderKycDocumentUploadPanel()
						) : (
							<KycSelfieVerificationPanel {...selfiePanelProps} />
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="kyc-details-wrapper">
			{isKycInteractionBlocked ? <div className="kyc-details-wrapper__interaction-blocker" aria-hidden="true" /> : null}
			<Wrapper isLoaderVisible={isGetKycFormFetching} isError={isGetKycFormError}>
				<>
					<form onSubmit={formik.handleSubmit}>
						<ul className="kyc-details-list row">
							<li className="d-col d-col-2 kyc-doc-main-container-li">
								{useIdfyKycFlowLayout ? renderIdfyKycColumn() : renderKycMainCard()}
							</li>

							{!checkIMRPermissions && (
								<li className="d-col d-col-2">
									<Dropdown
										id="IMR_Code"
										name="IMR_Code"
										label="IMR Code"
										title={formik.values.IMR_Code ?? ''}
										error={getErrorMessage('IMR_Code')}
										options={IMRCodeListData}
										setFieldValue={formik.setFieldValue}
										disabled={isEditing}
									/>
								</li>
							)}
						</ul>

						{checkRBDMPermission && !isImpersonationLoginUser && (
							<SubmitButton
								isEditing={isEditing}
								isPending={isKYCFormPending}
								isDisabled={isKYCFormPending || isImpersonationLoginUser}
								setIsEditing={setIsEditing}
								handleSubmitClick={handleSubmitClick}
							/>
						)}
					</form>
					<KycDocumentCameraModal
						isOpen={cameraModalOpen}
						stream={cameraStream}
						isCameraLoading={cameraLoading}
						onRequestCamera={requestDocumentCameraStream}
						onClose={closeDocumentCamera}
						onCapture={onDocumentCameraCapture}
					/>
				</>
			</Wrapper>
		</div>
	);
};
